export const STEPS = {
  ENTRY_POINT: "entry_point",
  // Held while a restored session's real step is resolved from /status.
  REHYDRATING: "rehydrating",
  SYLLABUS_UPLOAD: "syllabus_upload",
  ANALYZING: "analyzing",
  STATE_SELECT: "state_select",
  LOADING_STANDARDS: "loading_standards",
  FORM: "form",
  RECOMMENDING: "recommending",
  RECOMMENDATIONS: "recommendations",
  BUILDING: "building",
  COMPLETE: "complete",
  ERROR: "error",
};

export const ENTRY_TYPES = {
  // Key named for the flow; the wire value stays "upload" — it's stored in DB
  // sessions, S3 keys, and the create-session contract (mirrors the backend
  // SessionEntryType.SYLLABUS_UPLOAD member).
  SYLLABUS_UPLOAD: "upload",
  QUICK_START: "quick_start",
  STATE_STANDARDS: "state_standards",
};

// Discriminator on the POST /sessions/{id}/standards body. `profession` is the
// Transfr-taxonomy selection (state + profession); `subject` is the
// state-defined catalog (state + subject + optional sourceName drill-down).
export const STANDARDS_MODE = {
  PROFESSION: "profession",
  SUBJECT: "subject",
};

export const SESSION_STATUS = {
  CREATED: "created",
  UPLOADING: "uploading",
  ANALYZING: "analyzing",
  ANALYZED: "analyzed",
  LOADING_STANDARDS: "loading_standards",
  STANDARDS_LOADED: "standards_loaded",
  RECOMMENDING: "recommending",
  RECOMMENDED: "recommended",
  BUILT: "built",
  FAILED: "failed",
};

export const TERMINAL_LOADING_STATUSES = new Set([
  SESSION_STATUS.STANDARDS_LOADED,
  SESSION_STATUS.ANALYZED,
  SESSION_STATUS.RECOMMENDED,
  SESSION_STATUS.BUILT,
]);

// Syllabus upload constraints (ENG-23217) — mirror the backend's upload
// validation (core_platform upload.py: _ALLOWED_MIME_TYPES, 100 MB cap).
export const SYLLABUS_ACCEPTED_MIME_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
]);

// The native picker filter. Extensions rather than MIME types so the dialog
// behaves consistently across platforms.
export const SYLLABUS_FILE_ACCEPT = ".pdf,.docx,.jpg,.jpeg,.png";

export const SYLLABUS_MAX_FILE_MB = 100;
export const SYLLABUS_MAX_FILE_BYTES = SYLLABUS_MAX_FILE_MB * 1024 * 1024;

export const SYLLABUS_UPLOAD_ERRORS = {
  UNSUPPORTED_TYPE:
    "Unsupported file type. Please upload a PDF, Word document, or image.",
  TOO_LARGE: `File is too large. The maximum size is ${SYLLABUS_MAX_FILE_MB} MB.`,
  UPLOAD_FAILED: "Upload failed. Please check your connection and try again.",
};

// 409 `detail.code` values from POST /criteria for upload sessions
// (ENG-23216) — analysis problems are surfaced explicitly, never silently
// downgraded to a manual form.
export const CRITERIA_CONFLICT_CODES = {
  ANALYSIS_FAILED: "ANALYSIS_FAILED",
  ANALYSIS_EMPTY: "ANALYSIS_EMPTY",
  SESSION_NOT_READY: "SESSION_NOT_READY",
};

// Which pipeline stage a FAILED session died in (`failedStage` on /status,
// detail, and list responses). upload/analysis failures recover
// on the upload step (re-upload / convert to Quick Start); recommendation
// failures retry by re-submitting criteria.
export const FAILED_STAGES = {
  UPLOAD: "upload",
  ANALYSIS: "analysis",
  RECOMMENDATION: "recommendation",
};

export const CONTENT_TYPES = {
  SIMULATION: "simulation",
  CURRICULUM_RESOURCE: "curriculum_resource",
};

// Recommendation-quality feedback (ENG-23158). Wire values for the thumbs
// up/down control; null clears the rating.
export const FEEDBACK_RATING = {
  UP: "up",
  DOWN: "down",
};

// Recommendation `matchType` values emitted by the backend recommender.
// Today the recommender only emits LLM_RANKED — EXACT and FLEXIBLE are
// reserved for a future confidence-tier split (see backend recommender.py).
export const MATCH_TYPES = {
  EXACT: "exact",
  FLEXIBLE: "flexible",
  LLM_RANKED: "llm_ranked",
};

export const PROGRAM_TYPE_OPTIONS = [
  { id: "cte_course", name: "CTE Course" },
  { id: "academic_course", name: "Academic Course" },
  { id: "advisory", name: "Advisory" },
  { id: "alternative_program", name: "Alternative Program" },
  { id: "short_term_program", name: "Short-Term Program" },
  { id: "counseling", name: "Counseling" },
  { id: "pre_ets", name: "Pre-ETS" },
  { id: "justice_impacted", name: "Justice-Impacted" },
  { id: "other", name: "Other" },
];

export const USAGE_FREQUENCY_OPTIONS = [
  { id: "daily", name: "Daily" },
  { id: "twice_a_week", name: "Twice a week" },
  { id: "once_a_week", name: "Once a week" },
  { id: "once_a_month", name: "Once a month" },
  { id: "occasionally", name: "Occasionally" },
];

export const MATCH_FLEXIBILITY_OPTIONS = [
  { id: "exact", name: "Exact match (recommended)" },
  { id: "flexible", name: "Flexible match (broader results)" },
];

export const INTENT_OPTIONS = [
  { id: "explore_careers", name: "Explore careers" },
  { id: "build_technical_skills", name: "Build technical skills" },
  { id: "supplement_academic", name: "Supplement academic course" },
  { id: "structured_activities", name: "Structured activities" },
  { id: "transition_life_skills", name: "Transition / life skills" },
];

export const INDUSTRY_AREA_OPTIONS = [
  { id: "healthcare", name: "Healthcare" },
  { id: "it_cyber_technology", name: "IT, Cyber & Technology" },
  {
    id: "skilled_trades",
    name: "Skilled Trades (Construction, HVAC, Electrical, Welding)",
  },
  { id: "manufacturing_logistics", name: "Manufacturing & Logistics" },
  { id: "public_safety", name: "Public Safety & Protective Services" },
  { id: "business_customer_service", name: "Business & Customer Service" },
  { id: "education_human_services", name: "Education & Human Services" },
  { id: "other", name: "Other" },
  { id: "general_exploration", name: "General Exploration" },
];

export const ROLE_OPTIONS = [
  { id: "cte_instructor", name: "CTE instructor" },
  { id: "general_ed_teacher", name: "General ed teacher" },
  { id: "counselor_advisor", name: "Counselor / advisor" },
  { id: "supervision_duty", name: "Supervision duty" },
];

export const CLASS_TIME_PER_WEEK_OPTIONS = [
  { id: "lt_1h", name: "Under 1 hour" },
  { id: "1_3h", name: "1–3 hours" },
  { id: "3_5h", name: "3–5 hours" },
  { id: "gt_5h", name: "5+ hours" },
];

export const CRITERIA_FREE_TEXT_MAX = 2000;

// Presence of this param on the classroom route reopens the wizard after a
// refresh (ENG-23196).
export const AICA_SESSION_PARAM = "aicaSession";

export const DRAFT_SAVE_DEBOUNCE_MS = 400;

export const POLL_INTERVAL_MS = 2000;
// Must exceed the backend recommender ceiling (_LLM_REQUEST_TIMEOUT_MS = 15 min
// plus retries), or the UI gives up before generation finishes.
export const POLL_TIMEOUT_MS = 1_200_000; // 20 minutes
